import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { jobsQuery, type JobSummary } from '../api/jobs.ts';
import { localLabel } from '../api/quotes.ts';
import { PageHeader } from '../components/page-header.tsx';
import { fulfilmentLabel } from './fulfilment-panel.tsx';

const JOB_STATUS_LABELS: Record<JobSummary['status'], string> = {
  confirmed: 'Confirmed',
  out: 'Out',
  returned: 'Returned',
  cancelled: 'Cancelled',
};

// CB-REQ JOB-01 as built at v1.946. There is no "New job": a job is created by accepting a quote
// (QUO-01), so the way in is the quote.

export function JobsScreen() {
  const jobs = useQuery(jobsQuery);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Jobs"
        title="Jobs"
        meta={
          jobs.data ? `${jobs.data.length} ${jobs.data.length === 1 ? 'job' : 'jobs'}` : undefined
        }
      />
      {jobs.isPending ? <p className="text-muted">Loading…</p> : null}
      {jobs.isError ? (
        <p role="alert" className="alert">
          {jobs.error.message}
        </p>
      ) : null}
      {jobs.data?.length === 0 ? (
        <p className="text-ink-soft">
          No jobs yet. A job is created when a quote is accepted —{' '}
          <Link to="/quotes" className="link">
            open the quotes
          </Link>
          .
        </p>
      ) : null}
      {jobs.data && jobs.data.length > 0 ? <JobTable rows={jobs.data} /> : null}
    </div>
  );
}

function JobTable({ rows }: { rows: JobSummary[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <caption className="sr-only">Jobs, newest first</caption>
        <thead>
          <tr>
            <th scope="col">Job</th>
            <th scope="col">Client</th>
            <th scope="col">Goes out</th>
            <th scope="col">Comes back</th>
            <th scope="col">How</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((j) => (
            <tr key={j.id}>
              <th scope="row" className="font-semibold">
                <Link to="/jobs/$jobId" params={{ jobId: j.id }} className="link">
                  {j.reference}
                </Link>
                <span className="block text-sm font-normal text-ink-soft">
                  {j.name}
                  {j.type === 'produced_event' ? ' · Produced event' : ''}
                </span>
              </th>
              <td>{j.client.name}</td>
              <td className="text-sm">{localLabel(j.scheduledWindow.localStart)}</td>
              <td className="text-sm">{localLabel(j.scheduledWindow.localEnd)}</td>
              <td>
                <span className="tag">{fulfilmentLabel(j.fulfilment)}</span>
              </td>
              <td>
                {/* Cancelled and returned jobs stay in the list; this says which is which. */}
                <span className={j.status === 'out' ? 'tag tag-accent' : 'tag'}>
                  {JOB_STATUS_LABELS[j.status]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
